
import { 
  deepLearningForecast,
  peerComparisonAnalysis,
  preProductionPhaseAnalysis,
  gatherMarketIntelligence,
  type DeepLearningConfig,
  type PeerAnalysisConfig
} from '@/lib/deep-learning-models';
import { CollectedData } from './dataCollectionService';

export interface AnalysisResults {
  deepLearningResults: any;
  peerAnalysis: any;
  phaseAnalyses: Record<string, any>;
  marketIntelligence: any;
  historicalCashFlow: number[];
  dlConfig: DeepLearningConfig;
}

export async function runAnalysis(
  collectedData: CollectedData,
  setAnalysisStage: (stage: string) => void
): Promise<AnalysisResults> {
  const { balanceSheetData, assumptionsData, hasBalanceSheetData } = collectedData;

  // Stage 2: Deep Learning Analysis
  setAnalysisStage('Running deep learning models...');
  
  const historicalCashFlow = hasBalanceSheetData 
    ? balanceSheetData.map(entry => Number(entry.balance)).slice(0, 24)
    : [100000, 120000, 110000, 135000, 125000, 140000];

  const dlConfig: DeepLearningConfig = {
    modelType: 'ensemble',
    lookbackPeriod: 6,
    forecastHorizon: 12,
    confidenceLevel: 0.85,
    industryFocus: 'mining'
  };

  const deepLearningResults = deepLearningForecast(
    historicalCashFlow,
    dlConfig,
    assumptionsData?.assumptions_data || {}
  );

  // Stage 3: Peer Comparison Analysis
  setAnalysisStage('Analyzing peer comparisons...');
  
  const peerConfig: PeerAnalysisConfig = {
    region: 'Australia',
    mineralType: 'gold',
    projectStage: 'exploration',
    marketCap: 'small',
    includeGlobal: true
  };

  const peerAnalysis = peerComparisonAnalysis(
    {},
    peerConfig
  );

  // Stage 4: Pre-Production Phase Analysis
  setAnalysisStage('Analyzing project phases...');
  
  const phases = ['prospecting', 'exploration', 'development'] as const;
  const phaseAnalyses: Record<string, any> = {};
  
  for (const phase of phases) {
    phaseAnalyses[phase] = preProductionPhaseAnalysis(
      phase,
      {},
      {}
    );
  }

  // Stage 5: Market Intelligence
  setAnalysisStage('Gathering market intelligence...');
  
  const marketIntelligence = await gatherMarketIntelligence('gold', 'Australia');

  return {
    deepLearningResults,
    peerAnalysis,
    phaseAnalyses,
    marketIntelligence,
    historicalCashFlow,
    dlConfig
  };
}
